import React, { useState } from "react";
import { X, Printer, Download, Copy, CheckCheck } from "lucide-react";

const GenerateCardsModal = ({ isOpen, onClose }) => {
  const [numberOfCards, setNumberOfCards] = useState(4);
  const [gameType, setGameType] = useState("75");
  const [cardSize, setCardSize] = useState("standard");
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  // Game type options
  const gameTypeOptions = {
    15: { name: "15-Ball Express Bingo", icon: "🏎️" },
    30: { name: "30-Ball Speed Bingo", icon: "⚡" },
    50: { name: "50-Ball Quick Bingo", icon: "🔄" },
    75: { name: "75-Ball American Bingo", icon: "🇺🇸" },
    90: { name: "90-Ball British Bingo", icon: "🇬🇧" },
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      window.location.origin +
        "/printable-cards?count=" +
        numberOfCards +
        "&type=" +
        gameType
    );
    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <div className="flex justify-between items-center mb-5">
                  <h3
                    className="text-2xl leading-6 font-bold text-purple-700"
                    id="modal-title"
                  >
                    Generate Printable Cards
                  </h3>
                  <button
                    type="button"
                    className="bg-white rounded-full p-1 hover:bg-gray-100 focus:outline-none"
                    onClick={onClose}
                  >
                    <X size={24} className="text-gray-500" />
                  </button>
                </div>

                <div className="mt-4 space-y-6">
                  {/* Game Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Game Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(gameTypeOptions).map(
                        ([value, { name, icon }]) => (
                          <button
                            key={value}
                            onClick={() => setGameType(value)}
                            className={`flex items-center p-3 border rounded-md ${
                              gameType === value
                                ? "border-purple-500 bg-purple-50 text-purple-700"
                                : "border-gray-300 hover:border-purple-300"
                            }`}
                          >
                            <span className="text-xl mr-2">{icon}</span>
                            <span className="text-sm font-medium">{name}</span>
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Number of Cards */}
                  <div>
                    <label
                      htmlFor="card-count"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Number of Cards to Generate
                    </label>
                    <div className="mt-2 relative rounded-md shadow-sm">
                      <input
                        type="number"
                        id="card-count"
                        min="1"
                        max="100"
                        value={numberOfCards}
                        onChange={(e) => setNumberOfCards(e.target.value)}
                        className="focus:ring-purple-500 focus:border-purple-500 block w-full pl-4 pr-4 py-2 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Generate between 1 and 100 unique cards
                    </p>
                  </div>

                  {/* Card Style Options */}
                  <div>
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700">
                        Card Size
                      </legend>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center">
                          <input
                            id="standard"
                            name="cardSize"
                            type="radio"
                            checked={cardSize === "standard"}
                            onChange={() => setCardSize("standard")}
                            className="focus:ring-purple-500 h-4 w-4 text-purple-600 border-gray-300"
                          />
                          <label
                            htmlFor="standard"
                            className="ml-3 text-sm text-gray-700"
                          >
                            Standard (4 cards per page)
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            id="large"
                            name="cardSize"
                            type="radio"
                            checked={cardSize === "large"}
                            onChange={() => setCardSize("large")}
                            className="focus:ring-purple-500 h-4 w-4 text-purple-600 border-gray-300"
                          />
                          <label
                            htmlFor="large"
                            className="ml-3 text-sm text-gray-700"
                          >
                            Large (1 card per page)
                          </label>
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  {/* Header and Footer Options */}
                  <div className="flex space-x-6">
                    <div className="flex items-center">
                      <input
                        id="include-header"
                        type="checkbox"
                        checked={includeHeader}
                        onChange={() => setIncludeHeader(!includeHeader)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="include-header"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Include Header
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="include-footer"
                        type="checkbox"
                        checked={includeFooter}
                        onChange={() => setIncludeFooter(!includeFooter)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="include-footer"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Include Footer
                      </label>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-gray-50 rounded-md p-3 flex justify-center">
                    <div
                      className={`h-32 w-24 border border-gray-300 bg-white flex flex-col ${
                        includeHeader || includeFooter
                          ? "justify-between"
                          : "justify-center"
                      }`}
                    >
                      {includeHeader && (
                        <div className="text-center bg-purple-100 p-1 text-xs font-bold">
                          BINGO
                        </div>
                      )}
                      <div className="text-center text-xs font-medium text-gray-500">
                        {gameTypeOptions[gameType].icon} Card Preview
                      </div>
                      {includeFooter && (
                        <div className="text-center bg-gray-100 p-1 text-xs">
                          callmybingo.com
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => window.open("/printable-cards", "_blank")}
            >
              <Printer size={18} className="mr-1" />
              Print Cards
            </button>

            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleCopyLink}
            >
              {showCopiedMessage ? (
                <>
                  <CheckCheck size={18} className="mr-1 text-green-500" />
                  <span className="text-green-500">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={18} className="mr-1" />
                  <span>Copy Link</span>
                </>
              )}
            </button>

            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateCardsModal;
